import gql from 'graphql-tag';
import { ProjectPermissionsFields } from './project-permissions-fields.queries';

const ProjectRoomFields = gql`
	fragment ProjectRoomFields on ProjectRoom {
		id
		name
		image {
			id
			description
			imageType
		}
	}
`;

const ProjectGroupRoomFields = gql`
	fragment ProjectGroupRoomFields on ProjectGroup {
		room {
			id
			status
		}
	}
`;

const ProjectGroupFields = gql`
	${ProjectGroupRoomFields}
	fragment ProjectGroupFields on ProjectGroup {
		id
		name
		displayOrder
		cartId
		createdOn
		budget
		...ProjectGroupRoomFields
	}
`;

const ProjectFields = gql`
	${ProjectGroupFields}
	fragment ProjectFields on Project {
		id
		name
		created
		isArchived
		groups {
			...ProjectGroupFields
		}
		isDeleted
		description
		budget
		isLocked
		cartId
		relationType
		inviteId
	}
`;

const ProjectFieldsWithPermissions = gql`
	${ProjectFields}
	${ProjectPermissionsFields}
	fragment ProjectFieldsWithPermissions on Project {
		...ProjectFields
		permissions {
			...ProjectPermissionsFields
		}
	}
`;

export const PagedProjectsResultFields = gql`
	${ProjectFields}
	fragment PagedProjectsResultFields on PagedProjectResult {
		totalProjects
		projects {
			...ProjectFields
		}
		currentProject {
			id
			name
		}
	}
`;

const PagedProjectsResultFieldsWithPermissions = gql`
	${ProjectFieldsWithPermissions}
	fragment PagedProjectsResultFieldsWithPermissions on PagedProjectResult {
		totalProjects
		projects {
			...ProjectFieldsWithPermissions
		}
		currentProject {
			id
			name
		}
	}
`;

/** Queries */

export const GET_CURRENT_PROJECT = gql`
	query GetCurrentProject {
		projects {
			currentProject {
				id
				name
			}
		}
	}
`;

export const FIND_PROJECTS = gql`
	${PagedProjectsResultFields}
	query Projects($input: ProjectsSearchCriteriaInput) {
		projects(input: $input) {
			...PagedProjectsResultFields
		}
	}
`;

export const FIND_PROJECTS_WITH_PERMISSIONS = gql`
	${PagedProjectsResultFieldsWithPermissions}
	query ProjectsWithPermissions($input: ProjectsSearchCriteriaInput) {
		projects(input: $input) {
			...PagedProjectsResultFieldsWithPermissions
		}
	}
`;

export const PROJECT_ROOMS = gql`
	${ProjectRoomFields}
	query ProjectRooms {
		projectRooms {
			...ProjectRoomFields
		}
	}
`;

export const VERIFY_PROJECT_ACCESS = gql`
	query VerifyProjectAccess($input: VerifyAccessCriteria) {
		verifyProjectAccess(input: $input)
	}
`;

/** Mutations */

export const CREATE_PROJECT = gql`
	mutation CreateProject($name: String!, $roomIds: [Int!]!) {
		createProject(name: $name, roomIds: $roomIds)
	}
`;

export const CLONE_PROJECT = gql`
	mutation CloneProject($input: CloneProjectInput!) {
		cloneProject(input: $input) {
			... on CloneProject {
				id
			}
		}
	}
`;

export const CREATE_GROUP = gql`
	${ProjectGroupRoomFields}
	mutation CreateGroup($input: ProjectGroupInput!) {
		createGroup(input: $input) {
			... on ProjectGroup {
				id
				name
				createdOn
				displayOrder
				cartId
				budget
				...ProjectGroupRoomFields
			}
		}
	}
`;

export const CREATE_GROUPS = gql`
	${ProjectGroupRoomFields}
	mutation CreateGroups($input: [ProjectGroupInput!]!) {
		createGroups(input: $input) {
			... on ProjectGroup {
				id
				name
				createdOn
				displayOrder
				cartId
				budget
				...ProjectGroupRoomFields
			}
		}
	}
`;

export const ADD_PRODUCT_TO_PROJECTS = gql`
	mutation AddProductToProjects($input: AddToProjectsInput!) {
		addProductToProjects(input: $input) {
			shoppingListCartId
			newItemIds
		}
	}
`;

export const PROJECT_NAME = gql`
	query ProjectName($projectId: Int!) {
		projectById(id: $projectId, excludeGroups: true) {
			... on Project {
				name
			}
			... on NotFoundError {
				message
			}
		}
	}
`;

export const GET_ASSOCIATE_PROJECTS = gql`
	query AssociateProjects($input: AssociateProjectInput!) {
		associateProjects(input: $input) {
			totalProjects
			projects {
				id
				name
				description
				customerId
				customerEmail
				created
				lastModified
				archived
				isLocked
			}
		}
	}
`;
